import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/db';
import { useAuth } from '../context/AuthContext';
import { VoucherCode } from '../types';
import { ArrowLeft, PlusCircle, Search, FileText } from 'lucide-react';

// Define initial form state to ensure consistency
const initialVoucherFormState = {
  code: '',
  discountType: 'percentage' as 'percentage' | 'fixed',
  value: 0,
  partner: '',
  max_uses: 1, // Default to 1 as per database constraint
  remaining_uses: 1, // For new vouchers, this should match max_uses
  active: true,
  valid_until: null as string | null
};

const AdminVouchersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState<VoucherCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVouchers, setFilteredVouchers] = useState<VoucherCode[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherCode | null>(null);
  const [voucherForm, setVoucherForm] = useState(initialVoucherFormState);

  useEffect(() => {
    loadVouchers();
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      loadVouchers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadVouchers = async () => {
    try {
      // Call secure Edge Function for voucher data
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'get',
          searchTerm: searchTerm
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch vouchers');
      }

      const result = await response.json();
      setVouchers(result.data.vouchers);
      setFilteredVouchers(result.data.vouchers);
      
    } catch (error) {
      console.error('Error loading vouchers:', error);
      alert('Fehler beim Laden der Voucher: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVoucherEdit = (voucher: VoucherCode) => {
    setSelectedVoucher(voucher);
    setVoucherForm({
      code: voucher.code,
      discountType: voucher.discountType,
      value: voucher.value,
      partner: voucher.partner,
      max_uses: voucher.max_uses,
      remaining_uses: voucher.remaining_uses,
      active: voucher.active || true,
      valid_until: voucher.valid_until || null
    });
    setShowModal(true);
  };

  const handleVoucherToggle = async (voucherId: string, active: boolean) => {
    try {
      const voucher = vouchers.find(v => v.id === voucherId);
      if (!voucher) {
        alert('Voucher nicht gefunden');
        return;
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'toggle',
          voucherData: {
            id: voucherId,
            code: voucher.code,
            discountType: voucher.discountType,
            value: voucher.value,
            max_uses: voucher.max_uses,
            remaining_uses: voucher.remaining_uses,
            partner: voucher.partner,
            active: active,
            valid_until: voucher.valid_until
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle voucher');
      }

      // Update local state optimistically
      setVouchers(prev => 
        prev.map(v => v.id === voucherId ? { ...v, active } : v)
      );
      setFilteredVouchers(prev => 
        prev.map(v => v.id === voucherId ? { ...v, active } : v)
      );
      
    } catch (error) {
      console.error('Error updating voucher:', error);
      alert(`Fehler beim ${active ? 'Aktivieren' : 'Deaktivieren'} des Vouchers: ${error.message}`);
      // Reload vouchers to ensure consistency
      loadVouchers();
    }
  };

  const handleVoucherSubmit = async () => {
    try {
      // Enhanced validation with specific error messages
      if (!voucherForm.code.trim()) {
        alert('Bitte geben Sie einen Voucher-Code ein.');
        return;
      }

      // Validate value is a valid positive number
      if (!voucherForm.value || isNaN(voucherForm.value) || voucherForm.value <= 0) {
        alert('Bitte geben Sie einen gültigen Rabattwert größer als 0 ein.');
        return;
      }

      // Additional validation for percentage values
      if (voucherForm.discountType === 'percentage' && voucherForm.value > 100) {
        alert('Prozentuale Rabatte können nicht größer als 100% sein.');
        return;
      }

      // Validate max_uses is a valid positive integer
      if (!voucherForm.max_uses || isNaN(voucherForm.max_uses) || voucherForm.max_uses <= 0 || !Number.isInteger(voucherForm.max_uses)) {
        alert('Bitte geben Sie eine gültige Anzahl maximaler Nutzungen (ganze Zahl größer als 0) ein.');
        return;
      }

      // Validate remaining_uses for existing vouchers
      if (selectedVoucher && (isNaN(voucherForm.remaining_uses) || voucherForm.remaining_uses < 0 || !Number.isInteger(voucherForm.remaining_uses))) {
        alert('Bitte geben Sie eine gültige Anzahl verbleibender Nutzungen (ganze Zahl größer oder gleich 0) ein.');
        return;
      }

      // Ensure remaining_uses is not greater than max_uses
      const remaining_uses = selectedVoucher ? 
        Math.min(voucherForm.remaining_uses, voucherForm.max_uses) : 
        voucherForm.max_uses;

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const action = selectedVoucher ? 'update' : 'create';
      const voucherData = selectedVoucher ? {
        id: selectedVoucher.id,
        code: voucherForm.code.trim(),
        discountType: voucherForm.discountType,
        value: Number(voucherForm.value),
        max_uses: Number(voucherForm.max_uses),
        remaining_uses: Number(remaining_uses),
        partner: voucherForm.partner?.trim() || null,
        active: voucherForm.active,
        valid_until: voucherForm.valid_until
      } : {
        code: voucherForm.code.trim(),
        discountType: voucherForm.discountType,
        value: Number(voucherForm.value),
        max_uses: Number(voucherForm.max_uses),
        partner: voucherForm.partner?.trim() || null,
        active: voucherForm.active,
        valid_until: voucherForm.valid_until
      };

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: action,
          voucherData: voucherData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save voucher');
      }

      setShowModal(false);
      setVoucherForm(initialVoucherFormState);
      setSelectedVoucher(null);
      loadVouchers();
      
    } catch (error) {
      console.error('Error saving voucher:', error);
      alert(`Fehler beim Speichern des Vouchers: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin')}
              className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Voucherübersicht</h1>
          </div>
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => {
              setSelectedVoucher(null);
              setVoucherForm(initialVoucherFormState);
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center whitespace-nowrap"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Neuer Voucher
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rabatt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nutzungen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVouchers.map((voucher) => (
                  <tr key={voucher.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {voucher.code}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {voucher.partner}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {voucher.value}{voucher.discountType === 'percentage' ? '%' : '€'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {voucher.used_count} / {voucher.max_uses}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        voucher.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {voucher.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => navigate(`/admin/vouchers/${voucher.id}/report`)}
                        className="text-orange-600 hover:text-orange-800 mr-3 inline-flex items-center gap-1"
                        title="Report anzeigen"
                      >
                        <FileText className="h-4 w-4" />
                        Report
                      </button>
                      <button
                        onClick={() => handleVoucherEdit(voucher)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleVoucherToggle(voucher.id, !voucher.active)}
                        className={`${
                          voucher.active
                            ? 'text-red-600 hover:text-red-800'
                            : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {voucher.active ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Voucher Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-semibold mb-4">
                {selectedVoucher ? 'Voucher bearbeiten' : 'Neuer Voucher'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={voucherForm.code}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="z.B. WELCOME50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rabatttyp
                  </label>
                  <select
                    value={voucherForm.discountType}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, discountType: e.target.value as 'percentage' | 'fixed' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="percentage">Prozentual (%)</option>
                    <option value="fixed">Fester Betrag (€)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wert
                  </label>
                  <input
                    type="number"
                    value={voucherForm.value}
                    onChange={(e) => {
                      const value = e.target.value;
                     // Replace comma with dot for proper number parsing
                     const normalizedValue = value.replace(',', '.');
                      // Allow empty string for user to clear field, but convert to number when not empty
                      setVoucherForm(prev => ({ 
                        ...prev, 
                       value: value === '' ? 0 : Number(normalizedValue) 
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0.01"
                    step={voucherForm.discountType === 'percentage' ? '1' : '0.01'}
                    max={voucherForm.discountType === 'percentage' ? '100' : undefined}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gültig bis (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={voucherForm.valid_until || ''}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, valid_until: e.target.value || null }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Partner
                  </label>
                  <input
                    type="text"
                    value={voucherForm.partner}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, partner: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="z.B. Digital Academy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximale Nutzungen
                  </label>
                  <input
                    type="number"
                    value={voucherForm.max_uses}
                    onChange={(e) => {
                      const value = e.target.value;
                     // Replace comma with dot for proper number parsing
                     const normalizedValue = value.replace(',', '.');
                     const newMaxUses = value === '' ? 1 : Number(normalizedValue);
                      setVoucherForm(prev => ({ 
                        ...prev, 
                        max_uses: newMaxUses,
                        // For new vouchers, adjust remaining_uses to match max_uses
                        remaining_uses: selectedVoucher ? prev.remaining_uses : newMaxUses
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="1"
                    required
                  />
                </div>
                {selectedVoucher && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Verbleibende Nutzungen
                    </label>
                    <input
                      type="number"
                      value={voucherForm.remaining_uses}
                      onChange={(e) => {
                        const value = e.target.value;
                       // Replace comma with dot for proper number parsing
                       const normalizedValue = value.replace(',', '.');
                        setVoucherForm(prev => ({ 
                          ...prev, 
                         remaining_uses: value === '' ? 0 : Number(normalizedValue) 
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                      max={voucherForm.max_uses}
                      required
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setVoucherForm(initialVoucherFormState);
                    setSelectedVoucher(null);
                  }}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 mr-2"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleVoucherSubmit}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVouchersPage;